const BOM_SORT_MODE_GROUP = 'group';
const BOM_SORT_MODE_DRAWING_SHEET = 'drawing-sheet';
const BOM_DISPLAY_MODE_DETAILED = 'detailed';
const BOM_DISPLAY_MODE_COMPACT = 'compact';

// Der bisherige Total-Modus bleibt als fachlicher Bezeichner erhalten. Er wird
// im neuen BOM-Dialog nicht als Ansicht angeboten, kann aber von der spaeteren
// Stueckliste weiterhin eindeutig angesprochen werden.
const BOM_LEGACY_VIEW_MODE_TOTAL = 'total';

function normalizedText(value) {
  return String(value ?? '').trim();
}

function finiteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function normalizedSortOrder(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function normalizeBomSortMode(value) {
  return value === BOM_SORT_MODE_DRAWING_SHEET
    ? BOM_SORT_MODE_DRAWING_SHEET
    : BOM_SORT_MODE_GROUP;
}

function normalizeBomDisplayMode(value) {
  return value === BOM_DISPLAY_MODE_COMPACT
    ? BOM_DISPLAY_MODE_COMPACT
    : BOM_DISPLAY_MODE_DETAILED;
}

function bomSelectionKey(sheetId, groupKey) {
  return JSON.stringify([normalizedText(sheetId), normalizedText(groupKey)]);
}

function normalizeBomSourceGroup(value, sheet, index) {
  if (!value || typeof value !== 'object') return null;
  const scopeId = normalizedText(value.scopeId ?? value.bomScopeId ?? value.id);
  const groupId = normalizedText(value.groupId ?? value.sourceId ?? scopeId);
  const key = normalizedText(value.key ?? value.groupKey ?? scopeId ?? groupId);
  const systemId = normalizedText(value.systemId ?? value.systemKey ?? value.sourceSystemId);
  if (!key || !systemId) return null;
  const name = normalizedText(
    value.name
      ?? value.displayName
      ?? value.label
      ?? value.productLabel
      ?? groupId
      ?? key
  ) || key;
  const systemName = normalizedText(
    value.systemName
      ?? value.productLabel
      ?? value.systemLabel
      ?? systemId
  ) || systemId;
  const rows = Array.isArray(value.rows) ? value.rows.slice() : [];
  return {
    ...value,
    id: normalizedText(value.id) || scopeId || key,
    key,
    scopeId: scopeId || key,
    groupId: groupId || key,
    systemId,
    systemName,
    name,
    displayName: normalizedText(value.displayName) || name,
    sortOrder: normalizedSortOrder(value.sortOrder, index),
    sheetId: sheet.id,
    sheetTitle: sheet.title,
    selectionKey: bomSelectionKey(sheet.id, key),
    rows
  };
}

function normalizeBomDrawingSheetSources(values = []) {
  if (!Array.isArray(values)) return [];
  const result = [];
  const sheetIds = new Set();
  for (const [sheetIndex, value] of values.entries()) {
    if (!value || typeof value !== 'object') continue;
    const id = normalizedText(value.id ?? value.sheetId);
    if (!id || sheetIds.has(id)) continue;
    sheetIds.add(id);
    const title = normalizedText(value.title ?? value.sheetTitle) || `Zeichnung Nr. ${sheetIndex + 1}`;
    const sheet = {
      ...value,
      id,
      title,
      sortOrder: normalizedSortOrder(value.sortOrder, sheetIndex)
    };
    const groupKeys = new Set();
    sheet.groups = (Array.isArray(value.groups) ? value.groups : [])
      .map((group, groupIndex) => normalizeBomSourceGroup(group, sheet, groupIndex))
      .filter(group => {
        if (!group || groupKeys.has(group.key)) return false;
        groupKeys.add(group.key);
        return true;
      })
      .sort((left, right) => left.sortOrder - right.sortOrder);
    result.push(sheet);
  }
  return result.sort((left, right) => left.sortOrder - right.sortOrder);
}

function allBomSelectionKeys(sources = []) {
  return normalizeBomDrawingSheetSources(sources)
    .flatMap(sheet => sheet.groups.map(group => group.selectionKey));
}

function selectionValues(selection) {
  if (selection instanceof Set) return [...selection];
  if (Array.isArray(selection)) return selection;
  if (Array.isArray(selection?.selectedGroupKeys)) return selection.selectedGroupKeys;
  return null;
}

function createBomSelectionSet(sources = [], selection = null) {
  const knownKeys = new Set(allBomSelectionKeys(sources));
  const values = selectionValues(selection);
  if (values === null) return knownKeys;
  return new Set(values
    .map(value => normalizedText(value))
    .filter(value => knownKeys.has(value)));
}

function bomSheetSelectionState(sources = [], selection = null, sheetId = '') {
  const sheets = normalizeBomDrawingSheetSources(sources);
  const sheet = sheets.find(entry => entry.id === normalizedText(sheetId));
  if (!sheet) {
    return {
      checked: false,
      indeterminate: false,
      disabled: true,
      selectedCount: 0,
      totalCount: 0
    };
  }
  const selected = createBomSelectionSet(sheets, selection);
  const groupKeys = sheet.groups.map(group => group.selectionKey);
  const selectedCount = groupKeys.filter(key => selected.has(key)).length;
  return {
    checked: groupKeys.length > 0 && selectedCount === groupKeys.length,
    indeterminate: selectedCount > 0 && selectedCount < groupKeys.length,
    disabled: groupKeys.length === 0,
    selectedCount,
    totalCount: groupKeys.length
  };
}

function toggleBomSheetSelection(sources = [], selection = null, sheetId = '', checked = true) {
  const sheets = normalizeBomDrawingSheetSources(sources);
  const selected = createBomSelectionSet(sheets, selection);
  const sheet = sheets.find(entry => entry.id === normalizedText(sheetId));
  for (const group of sheet?.groups || []) {
    if (checked) selected.add(group.selectionKey);
    else selected.delete(group.selectionKey);
  }
  return selected;
}

function toggleBomGroupSelection(sources = [], selection = null, sheetId = '', groupKey = '', checked = true) {
  const sheets = normalizeBomDrawingSheetSources(sources);
  const selected = createBomSelectionSet(sheets, selection);
  const sheet = sheets.find(entry => entry.id === normalizedText(sheetId));
  const group = sheet?.groups.find(entry => entry.key === normalizedText(groupKey));
  if (!group) return selected;
  if (checked) selected.add(group.selectionKey);
  else selected.delete(group.selectionKey);
  return selected;
}

function selectedBomDrawingSheetSources(sources = [], selection = null, options = {}) {
  const sheets = normalizeBomDrawingSheetSources(sources);
  const selected = createBomSelectionSet(sheets, selection);
  return sheets
    .map(sheet => ({
      ...sheet,
      groups: sheet.groups.filter(group => selected.has(group.selectionKey))
    }))
    .filter(sheet => options.includeEmptySheets === true || sheet.groups.length > 0);
}

function bomRowsTotal(rows = []) {
  const result = {
    rowCount: 0,
    quantity: 0,
    weightKg: 0,
    priceChf: 0,
    vpChf: 0,
    currency: 'CHF',
    hasUnknownArticle: false
  };
  for (const row of rows || []) {
    if (!row || typeof row !== 'object') continue;
    const quantity = finiteNumber(row.quantity ?? row.menge, 0);
    const unitWeight = Math.max(0, finiteNumber(row.weightKg ?? row.weight, 0));
    const price = finiteNumber(row.priceChf, 0);
    const salePrice = finiteNumber(row.vpChf, 0);
    result.rowCount += 1;
    result.quantity += quantity;
    result.weightKg += quantity * unitWeight;
    result.priceChf += quantity * price;
    result.vpChf += quantity * salePrice;
    result.hasUnknownArticle ||= row.isUnknownArticle === true;
    if (row.currency) result.currency = normalizedText(row.currency) || result.currency;
  }
  return result;
}

function addBomTotals(values = []) {
  const result = bomRowsTotal();
  for (const value of values || []) {
    if (!value || typeof value !== 'object') continue;
    result.rowCount += finiteNumber(value.rowCount, 0);
    result.quantity += finiteNumber(value.quantity, 0);
    result.weightKg += finiteNumber(value.weightKg, 0);
    result.priceChf += finiteNumber(value.priceChf, 0);
    result.vpChf += finiteNumber(value.vpChf, 0);
    result.hasUnknownArticle ||= value.hasUnknownArticle === true;
    if (value.currency) result.currency = normalizedText(value.currency) || result.currency;
  }
  return result;
}

function preparedPresentationGroup(group, sheet, options = {}) {
  const prepareRows = typeof options.prepareGroupRows === 'function'
    ? options.prepareGroupRows
    : rows => rows;
  const prepared = prepareRows(group.rows.slice(), {
    sheet,
    group,
    sortMode: normalizeBomSortMode(options.sortMode),
    displayMode: normalizeBomDisplayMode(options.displayMode)
  });
  const rows = Array.isArray(prepared) ? prepared : [];
  return {
    ...group,
    sheetId: sheet.id,
    sheetTitle: sheet.title,
    rows,
    totals: bomRowsTotal(rows)
  };
}

function systemSectionKey(group) {
  return `system:${group.systemId}`;
}

function createGroupSortedSections(sheets, options = {}) {
  const sectionsByKey = new Map();
  for (const sheet of sheets) {
    for (const group of sheet.groups) {
      const preparedGroup = preparedPresentationGroup(group, sheet, options);
      const key = systemSectionKey(preparedGroup);
      if (!sectionsByKey.has(key)) {
        sectionsByKey.set(key, {
          id: key,
          key,
          kind: 'system',
          systemId: preparedGroup.systemId,
          title: preparedGroup.systemName,
          groups: []
        });
      }
      sectionsByKey.get(key).groups.push(preparedGroup);
    }
  }
  return [...sectionsByKey.values()].map(section => ({
    ...section,
    totals: addBomTotals(section.groups.map(group => group.totals))
  }));
}

function createDrawingSheetSortedSections(sheets, options = {}) {
  return sheets.map(sheet => {
    const groups = sheet.groups.map(group => preparedPresentationGroup(group, sheet, options));
    return {
      id: `sheet:${sheet.id}`,
      key: `sheet:${sheet.id}`,
      kind: 'drawing-sheet',
      sheetId: sheet.id,
      title: sheet.title,
      groups,
      totals: addBomTotals(groups.map(group => group.totals))
    };
  });
}

function createBomPresentationEntries(sections = [], options = {}) {
  const sortMode = normalizeBomSortMode(options.sortMode);
  const displayMode = normalizeBomDisplayMode(options.displayMode);
  const entries = [];
  for (const section of sections) {
    entries.push({ type: 'section-header', section });
    if (displayMode === BOM_DISPLAY_MODE_DETAILED || sortMode === BOM_SORT_MODE_GROUP) {
      for (const group of section.groups) {
        if (displayMode === BOM_DISPLAY_MODE_DETAILED) {
          entries.push({ type: 'group-header', section, group });
          for (const row of group.rows) entries.push({ type: 'row', section, group, row });
        }
        entries.push({ type: 'group-subtotal', section, group, totals: group.totals });
      }
    }
    entries.push({ type: 'section-total', section, totals: section.totals });
  }
  return entries;
}

function createBomPresentation(sources = [], options = {}) {
  const sortMode = normalizeBomSortMode(options.sortMode);
  const displayMode = normalizeBomDisplayMode(options.displayMode);
  const selectedSheets = selectedBomDrawingSheetSources(sources, options.selection);
  const preparedOptions = { ...options, sortMode, displayMode };
  const sections = sortMode === BOM_SORT_MODE_DRAWING_SHEET
    ? createDrawingSheetSortedSections(selectedSheets, preparedOptions)
    : createGroupSortedSections(selectedSheets, preparedOptions);
  const totals = addBomTotals(sections.map(section => section.totals));
  const entries = createBomPresentationEntries(sections, { sortMode, displayMode });
  entries.push({ type: 'overall-total', totals });
  return {
    sortMode,
    displayMode,
    selectedSheetCount: selectedSheets.length,
    selectedGroupCount: selectedSheets.reduce((sum, sheet) => sum + sheet.groups.length, 0),
    sections,
    entries,
    totals
  };
}

function createBomDrawingSheetSource(sheet = {}, scopes = [], rowsForScope = () => []) {
  const sheetId = normalizedText(sheet.id ?? sheet.sheetId);
  if (!sheetId) return null;
  const source = {
    id: sheetId,
    title: normalizedText(sheet.title ?? sheet.sheetTitle) || 'Zeichnungsebene',
    sortOrder: normalizedSortOrder(sheet.sortOrder, 0),
    groups: (Array.isArray(scopes) ? scopes : []).map((scope, index) => {
      const scopeId = normalizedText(scope?.id ?? scope?.scopeId);
      const key = normalizedText(scope?.key ?? scopeId);
      const systemId = normalizedText(scope?.systemId ?? scope?.systemKey);
      if (!scopeId || !key || !systemId) return null;
      const rows = rowsForScope(scope, sheet);
      return {
        id: scopeId,
        key,
        scopeId,
        groupId: normalizedText(scope.groupId ?? scope.sourceId ?? scopeId),
        systemId,
        systemName: normalizedText(scope.systemName ?? scope.productLabel ?? scope.systemLabel ?? systemId),
        name: normalizedText(scope.name ?? scope.displayName ?? scope.label ?? scopeId),
        displayName: normalizedText(scope.displayName ?? scope.name ?? scope.label ?? scopeId),
        sidebarMeta: normalizedText(scope.sidebarMeta),
        sortOrder: normalizedSortOrder(scope.sortOrder, index),
        rows: Array.isArray(rows) ? rows : []
      };
    }).filter(Boolean)
  };
  return normalizeBomDrawingSheetSources([source])[0] || null;
}

export {
  BOM_DISPLAY_MODE_COMPACT,
  BOM_DISPLAY_MODE_DETAILED,
  BOM_LEGACY_VIEW_MODE_TOTAL,
  BOM_SORT_MODE_DRAWING_SHEET,
  BOM_SORT_MODE_GROUP,
  addBomTotals,
  allBomSelectionKeys,
  bomRowsTotal,
  bomSelectionKey,
  bomSheetSelectionState,
  createBomDrawingSheetSource,
  createBomPresentation,
  createBomPresentationEntries,
  createBomSelectionSet,
  normalizeBomDisplayMode,
  normalizeBomDrawingSheetSources,
  normalizeBomSortMode,
  selectedBomDrawingSheetSources,
  toggleBomGroupSelection,
  toggleBomSheetSelection
};
